import { memo, useCallback, useDeferredValue, useEffect, useMemo, useState } from "react";
import { VList } from "virtua";
import { Link } from "react-router-dom";
import {
  KundenartikelListSchema,
  type Kundenartikel,
  type KundenartikelList,
} from "@bidi-handel/core/schemas";
import { api, ApiError } from "../../api/client";
import { useApi } from "../../hooks/useApi";
import {
  CSV_HEADER_SYNONYMS,
  useCsvImport,
  type CsvImportField,
  type CsvImportMapping,
} from "../../hooks/useCsvImport";
import { useTenant } from "../../app/TenantContext";

/**
 * Kundenartikel-Editor:
 *  - „Liste"-Modus: Tabelle mit Inline-Edit + Delete + Add + Save.
 *  - „CSV importieren"-Modus: Drag&Drop oder File-Input, Auto-Detect der
 *    Spalten-Header, Mapping-Preview, Validate + Import (Replace-Modus).
 */

type Mode = "list" | "csv";

interface DraftArtikel extends Kundenartikel {
  _key: string;
}
let keyCounter = 0;
const nextKey = () => `a${++keyCounter}-${Date.now()}`;
const withKeys = (l: KundenartikelList): DraftArtikel[] =>
  l.map((a) => ({ ...a, _key: nextKey() }));
const stripKeys = (d: DraftArtikel[]): KundenartikelList =>
  d.map(({ _key, ...rest }) => {
    void _key;
    return rest;
  });

export function ArtikelPage() {
  const { tenantId } = useTenant();
  const [mode, setMode] = useState<Mode>("list");
  const { state, refetch } = useApi(
    () => api.getKundenartikel(tenantId),
    [tenantId]
  );
  // Summary fuer source-Detection: im Catalog/Sortiment-Modus ist dieser
  // Legacy-Editor wirkungslos (Loader liest aus Sortiment, Legacy-PUTs landen
  // in einer Datei, die nicht mehr gelesen wird). UI wird dann gelockt.
  const summaryRes = useApi(
    () => api.getStammdatenSummary(tenantId),
    [tenantId]
  );
  if (
    summaryRes.state.status === "ok" &&
    summaryRes.state.data.artikel.source === "catalog"
  ) {
    return (
      <CatalogModeLock
        tenantId={tenantId}
        count={summaryRes.state.data.artikel.gesamt}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setMode("list")}
          className={`rounded-md px-3 py-1.5 text-sm font-medium ${
            mode === "list"
              ? "bg-brand-600 text-white"
              : "border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
          }`}
        >
          Liste
        </button>
        <button
          type="button"
          onClick={() => setMode("csv")}
          className={`rounded-md px-3 py-1.5 text-sm font-medium ${
            mode === "csv"
              ? "bg-brand-600 text-white"
              : "border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
          }`}
        >
          CSV importieren
        </button>
      </div>

      {mode === "list" && (
        <ListEditor tenantId={tenantId} state={state} refetch={refetch} />
      )}
      {mode === "csv" && (
        <CsvImport
          tenantId={tenantId}
          current={state.status === "ok" ? state.data : []}
          onImported={() => {
            refetch();
            setMode("list");
          }}
        />
      )}
    </div>
  );
}

/**
 * Read-only-Lock-Screen wenn der Tenant im Catalog/Sortiment-Modus laeuft.
 * Vermeidet den stillen-Import-Pitfall (Legacy-PUT schreibt eine Datei, die
 * der StammdatenLoader nach G1 nicht mehr liest, weil Sortiment vorrangig
 * ist) durch klares Sperren der Edit-/Import-UIs und Wegweiser zum richtigen
 * Editor.
 */
function CatalogModeLock({ tenantId, count }: { tenantId: string; count: number }) {
  void tenantId;
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-6">
      <div className="flex items-start gap-3">
        <span aria-hidden="true" className="text-2xl">🔒</span>
        <div className="flex-1 space-y-3">
          <div>
            <h2 className="text-base font-semibold text-amber-900">
              Sortiment-Modus aktiv — Legacy-Editor gesperrt
            </h2>
            <p className="mt-1 text-sm text-amber-800">
              Dieser Tenant nutzt den <strong>Catalog &amp; Sortiment</strong>-Workflow.
              Die <em>Kundenartikel</em>-Liste wird daraus abgeleitet (
              <span className="font-mono">{count}</span> Artikel).
              Eintraege hier auf der <em>Legacy</em>-Seite werden vom System
              ignoriert — auch CSV-Importe.
            </p>
          </div>
          <div className="rounded-md border border-amber-200 bg-white p-3 text-xs text-slate-700">
            <p className="font-medium text-slate-900">Was tun?</p>
            <ul className="mt-1 list-inside list-disc space-y-1">
              <li>
                Artikel pflegen/importieren ueber{" "}
                <Link
                  to="/stammdaten/catalog"
                  className="font-medium text-brand-700 underline"
                >
                  01 Catalog &amp; Sortiment
                </Link>
              </li>
              <li>
                Dieser Tab bleibt nur zur Diagnose erreichbar — fuer Lese-
                Zugriff auf die <span className="font-mono">kundenartikel.json</span>
                {" "}auf der Disk (selten relevant).
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// LIST EDITOR
// ─────────────────────────────────────────────────────────────────

function ListEditor({
  tenantId,
  state,
  refetch,
}: {
  tenantId: string;
  state: ReturnType<typeof useApi<KundenartikelList>>["state"];
  refetch: () => void;
}) {
  const [drafts, setDrafts] = useState<DraftArtikel[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    if (state.status === "ok") {
      setDrafts(withKeys(state.data));
      setSaved(false);
    }
  }, [state.status, state.data]);

  // useDeferredValue laesst das Input responsive bleiben, waehrend die
  // gefilterte Liste mit Rueckstand neu gerendert wird.
  const deferredFilter = useDeferredValue(filter);
  const filtered = useMemo(() => {
    const q = deferredFilter.trim().toLowerCase();
    if (!q) return drafts;
    return drafts.filter(
      (a) =>
        a.code.toLowerCase().includes(q) ||
        a.bezeichnung.toLowerCase().includes(q) ||
        (a.warengruppe ?? "").toLowerCase().includes(q)
    );
  }, [drafts, deferredFilter]);

  // Stabile Callbacks fuer ArtikelRowEditor — damit React.memo greift und
  // beim Tippen in einer Zeile nicht alle 50k Rows re-rendern.
  const update = useCallback((key: string, patch: Partial<Kundenartikel>) => {
    setDrafts((ds) => ds.map((d) => (d._key === key ? { ...d, ...patch } : d)));
    setSaved(false);
  }, []);
  const remove = useCallback((key: string) => {
    setDrafts((ds) => ds.filter((d) => d._key !== key));
    setSaved(false);
  }, []);
  function add() {
    setDrafts((ds) => [
      { _key: nextKey(), code: "", bezeichnung: "", suchbegriffe: [] },
      ...ds,
    ]);
    setSaved(false);
  }

  async function handleSave() {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const cleaned = stripKeys(drafts);
      const validated = KundenartikelListSchema.parse(cleaned);
      const persisted = await api.putKundenartikel(tenantId, validated);
      setDrafts(withKeys(persisted));
      setSaved(true);
      refetch();
    } catch (err) {
      setError(formatError(err));
    } finally {
      setSaving(false);
    }
  }

  if (state.status === "loading")
    return <p className="text-sm text-slate-500">Lade Kundenartikel …</p>;
  if (state.status === "error")
    return (
      <ErrorBanner
        title="Kundenartikel nicht ladbar"
        error={state.error.message}
        onRetry={refetch}
      />
    );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <p className="text-xs text-slate-500">
          {drafts.length} Artikel
          {filter && ` · ${filtered.length} im Filter`}
        </p>
        <div className="flex gap-2">
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.currentTarget.value)}
            placeholder="Filter Code / Bezeichnung / Warengruppe …"
            className="input w-80"
          />
          <button
            type="button"
            onClick={add}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
          >
            + Neuer Artikel
          </button>
        </div>
      </div>

      {/* Virtualisierte Liste — bei 50k Artikeln ohne VList unscrollbar.
          Header + Rows teilen denselben grid-template-columns. */}
      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <div
          className="bg-slate-50 border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wider text-slate-500"
          style={{ display: "grid", gridTemplateColumns: ARTIKEL_GRID_COLS }}
        >
          <div className="px-3 py-2">Code</div>
          <div className="px-3 py-2">Bezeichnung</div>
          <div className="px-3 py-2">Warengruppe</div>
          <div className="px-3 py-2 text-right">Listenpreis</div>
          <div className="px-3 py-2">Einheit</div>
          <div className="px-3 py-2">Suchbegriffe</div>
          <div className="px-2 py-2"></div>
        </div>
        {filtered.length === 0 ? (
          <div className="px-3 py-6 text-center text-sm text-slate-500">
            {filter
              ? "Keine Artikel passen zum Filter."
              : "Noch keine Artikel — entweder '+ Neuer Artikel' oder via CSV importieren."}
          </div>
        ) : (
          <VList style={{ height: "min(70vh, 720px)" }}>
            {filtered.map((row) => (
              <ArtikelRowEditor
                key={row._key}
                row={row}
                onUpdate={update}
                onRemove={remove}
              />
            ))}
          </VList>
        )}
      </div>

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm">
          <p className="font-medium text-rose-800">Speichern fehlgeschlagen</p>
          <p className="mt-1 font-mono text-xs text-rose-700">{error}</p>
        </div>
      )}

      <div className="flex items-center justify-end gap-3">
        {saved && (
          <span className="text-xs font-medium text-emerald-700">✓ gespeichert</span>
        )}
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-700 disabled:opacity-50"
        >
          {saving ? "Speichere …" : "Speichern"}
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// CSV IMPORT
// ─────────────────────────────────────────────────────────────────

function CsvImport({
  tenantId,
  current,
  onImported,
}: {
  tenantId: string;
  current: KundenartikelList;
  onImported: () => void;
}) {
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const {
    csvRows,
    headers,
    mapping,
    setMapping,
    parseError,
    dragging,
    setDragging,
    handleFileInput,
    handleDrop,
    reset: resetParser,
  } = useCsvImport({ onBeforeParse: () => setImportError(null) });

  const previewArtikel: KundenartikelList | null = useMemo(() => {
    if (!csvRows || !mapping) return null;
    return csvRows.map((row) => buildArtikel(row, mapping));
  }, [csvRows, mapping]);

  const validation = useMemo(() => {
    if (!previewArtikel) return null;
    const parsed = KundenartikelListSchema.safeParse(previewArtikel);
    if (parsed.success) return { ok: true as const, count: parsed.data.length };
    return {
      ok: false as const,
      issues: parsed.error.issues.slice(0, 5).map((i) => ({
        path: i.path.join("."),
        message: i.message,
      })),
      totalIssues: parsed.error.issues.length,
    };
  }, [previewArtikel]);

  async function handleImport() {
    if (!previewArtikel || !validation?.ok) return;
    setImporting(true);
    setImportError(null);
    try {
      const validated = KundenartikelListSchema.parse(previewArtikel);
      await api.putKundenartikel(tenantId, validated);
      onImported();
    } catch (err) {
      setImportError(formatError(err));
    } finally {
      setImporting(false);
    }
  }

  function resetAll() {
    resetParser();
    setImportError(null);
  }

  if (!csvRows) {
    return (
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        className={`rounded-lg border-2 border-dashed p-12 text-center transition ${
          dragging
            ? "border-brand-500 bg-brand-50"
            : "border-slate-300 bg-white"
        }`}
      >
        <p className="text-sm font-medium text-slate-700">
          CSV-Datei hier ablegen
        </p>
        <p className="mt-1 text-xs text-slate-500">
          oder
          <label className="ml-1 cursor-pointer text-brand-700 underline">
            Datei waehlen
            <input
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={handleFileInput}
            />
          </label>
        </p>
        <p className="mt-3 text-xs text-slate-400">
          Erkennt Komma- oder Semikolon-Trenner automatisch · Header-Zeile
          benoetigt · Standard-Spalten <code>code</code>,{" "}
          <code>bezeichnung</code>,{" "}
          <code>listenpreis</code>, <code>einheit</code>, <code>warengruppe</code>{" "}
          werden auto-erkannt
        </p>
        {parseError && (
          <p className="mt-3 text-xs text-rose-600">{parseError}</p>
        )}
        {current.length > 0 && (
          <p className="mt-4 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700">
            ⚠ Import ersetzt die bestehende Liste mit {current.length} Artikel
            komplett. Backup empfohlen!
          </p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600">
          {csvRows.length} Zeilen geparst aus CSV
        </p>
        <button
          type="button"
          onClick={resetAll}
          className="text-xs font-medium text-brand-700 hover:underline"
        >
          ← Andere Datei waehlen
        </button>
      </div>

      <section>
        <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-slate-500">
          Spalten-Zuordnung
        </h3>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          {(Object.keys(CSV_HEADER_SYNONYMS) as CsvImportField[]).map((field) => (
            <label key={field} className="flex items-center gap-2 text-sm">
              <span className="w-32 font-mono text-xs text-slate-500">
                {field}
              </span>
              <select
                value={mapping?.[field] ?? ""}
                onChange={(e) =>
                  setMapping((m) =>
                    m
                      ? { ...m, [field]: e.currentTarget.value || null }
                      : m
                  )
                }
                className="input flex-1 text-xs"
              >
                <option value="">— ignorieren —</option>
                {headers.map((h) => (
                  <option key={h} value={h}>
                    {h}
                  </option>
                ))}
              </select>
            </label>
          ))}
        </div>
      </section>

      <section>
        <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-slate-500">
          Preview (erste 5 Zeilen)
        </h3>
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-50 text-left">
              <tr>
                <th className="px-2 py-1">code</th>
                <th className="px-2 py-1">bezeichnung</th>
                <th className="px-2 py-1">warengruppe</th>
                <th className="px-2 py-1 text-right">listenpreis</th>
                <th className="px-2 py-1">einheit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {previewArtikel?.slice(0, 5).map((a, idx) => (
                <tr key={idx}>
                  <td className="px-2 py-1 font-mono">{a.code || <em className="text-rose-500">fehlt</em>}</td>
                  <td className="px-2 py-1">{a.bezeichnung || <em className="text-rose-500">fehlt</em>}</td>
                  <td className="px-2 py-1">{a.warengruppe ?? "—"}</td>
                  <td className="px-2 py-1 text-right">{a.listenpreis ?? "—"}</td>
                  <td className="px-2 py-1">{a.einheit ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {validation && !validation.ok && (
        <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm">
          <p className="font-medium text-rose-800">
            {validation.totalIssues} Validation-Fehler — Mapping korrigieren oder CSV cleanen
          </p>
          <ul className="mt-2 space-y-1 text-xs text-rose-700">
            {validation.issues.map((iss, idx) => (
              <li key={idx} className="font-mono">
                {iss.path}: {iss.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      {validation && validation.ok && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm">
          <p className="font-medium text-emerald-800">
            ✓ {validation.count} Artikel passen ins Schema — bereit zum Import.
            <span className="ml-2 font-normal text-emerald-700">
              Import ersetzt bestehende Liste komplett.
            </span>
          </p>
        </div>
      )}

      {importError && (
        <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm">
          <p className="font-medium text-rose-800">Import fehlgeschlagen</p>
          <p className="mt-1 font-mono text-xs text-rose-700">{importError}</p>
        </div>
      )}

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleImport}
          disabled={!validation?.ok || importing}
          className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-700 disabled:opacity-50"
        >
          {importing ? "Importiere …" : `${csvRows.length} Artikel importieren (Replace)`}
        </button>
      </div>
    </div>
  );
}

// Grid-Spalten fuer Header + Rows (geteilt).
//   code · bezeichnung(flex) · warengruppe · listenpreis · einheit · suchbegriffe · delete
const ARTIKEL_GRID_COLS =
  "minmax(110px,140px) minmax(180px,1fr) minmax(120px,160px) minmax(100px,120px) minmax(80px,100px) minmax(180px,1fr) 44px";

const ArtikelRowEditor = memo(function ArtikelRowEditor({
  row,
  onUpdate,
  onRemove,
}: {
  row: DraftArtikel;
  onUpdate: (key: string, patch: Partial<Kundenartikel>) => void;
  onRemove: (key: string) => void;
}) {
  return (
    <div
      className="border-b border-slate-100"
      style={{
        display: "grid",
        gridTemplateColumns: ARTIKEL_GRID_COLS,
        alignItems: "center",
      }}
    >
      <div className="px-2 py-1">
        <input
          type="text"
          value={row.code}
          onChange={(e) => onUpdate(row._key, { code: e.currentTarget.value })}
          className="input w-full font-mono text-xs"
        />
      </div>
      <div className="px-2 py-1">
        <input
          type="text"
          value={row.bezeichnung}
          onChange={(e) =>
            onUpdate(row._key, { bezeichnung: e.currentTarget.value })
          }
          className="input w-full text-xs"
        />
      </div>
      <div className="px-2 py-1">
        <input
          type="text"
          value={row.warengruppe ?? ""}
          onChange={(e) =>
            onUpdate(row._key, {
              warengruppe: e.currentTarget.value || undefined,
            })
          }
          className="input w-full text-xs"
        />
      </div>
      <div className="px-2 py-1">
        <input
          type="number"
          min={0}
          step={0.01}
          value={row.listenpreis ?? ""}
          onChange={(e) =>
            onUpdate(row._key, {
              listenpreis:
                e.currentTarget.value === ""
                  ? undefined
                  : Number(e.currentTarget.value),
            })
          }
          className="input w-full text-right text-xs"
        />
      </div>
      <div className="px-2 py-1">
        <input
          type="text"
          value={row.einheit ?? ""}
          onChange={(e) =>
            onUpdate(row._key, { einheit: e.currentTarget.value || undefined })
          }
          className="input w-full text-xs"
          placeholder="m2"
        />
      </div>
      <div className="px-2 py-1">
        <input
          type="text"
          value={row.suchbegriffe.join(", ")}
          onChange={(e) =>
            onUpdate(row._key, {
              suchbegriffe: e.currentTarget.value
                .split(/[,;]/)
                .map((s) => s.trim())
                .filter(Boolean),
            })
          }
          className="input w-full text-xs"
          placeholder="komma- oder semikolon-separiert"
        />
      </div>
      <div className="px-2 py-1 text-center">
        <button
          type="button"
          onClick={() => onRemove(row._key)}
          className="rounded-md p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
          aria-label="Loeschen"
        >
          ✕
        </button>
      </div>
    </div>
  );
});

function buildArtikel(
  row: Record<string, string>,
  mapping: CsvImportMapping
): Kundenartikel {
  const get = (field: CsvImportField): string | undefined => {
    const col = mapping[field];
    if (!col) return undefined;
    const v = row[col]?.trim();
    return v && v !== "" ? v : undefined;
  };
  const numeric = (field: CsvImportField): number | undefined => {
    const raw = get(field);
    if (!raw) return undefined;
    const n = Number(raw.replace(",", "."));
    return Number.isFinite(n) ? n : undefined;
  };
  const suchbegriffeRaw = get("suchbegriffe") ?? "";
  return {
    code: get("code") ?? "",
    bezeichnung: get("bezeichnung") ?? "",
    kurzText: get("kurzText"),
    ean: get("ean"),
    vpe: get("vpe"),
    listenpreis: numeric("listenpreis"),
    einheit: get("einheit"),
    warengruppe: get("warengruppe"),
    suchbegriffe: suchbegriffeRaw
      .split(/[,;|]/)
      .map((s) => s.trim())
      .filter(Boolean),
  };
}

function formatError(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { hint?: string; error?: string; issues?: unknown[] } | null;
    if (body?.issues) {
      return body.issues.map((i: unknown) => JSON.stringify(i)).join(" · ");
    }
    return body?.hint ?? body?.error ?? err.message;
  }
  if (err instanceof Error) return err.message;
  return String(err);
}

function ErrorBanner({
  title,
  error,
  onRetry,
}: {
  title: string;
  error: string;
  onRetry: () => void;
}) {
  return (
    <div className="rounded-md border border-rose-200 bg-rose-50 p-4">
      <p className="text-sm font-medium text-rose-800">{title}</p>
      <p className="mt-1 font-mono text-xs text-rose-700">{error}</p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-2 rounded-md border border-rose-300 bg-white px-3 py-1 text-xs font-medium text-rose-700 hover:bg-rose-50"
      >
        Nochmal versuchen
      </button>
    </div>
  );
}
